import React, { useState, useEffect } from "react";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { zodResolver } from "@hookform/resolvers/zod";

import { Controller, useForm } from "react-hook-form";

import { useCoastguard } from "@cofano/coastguard2-frontend-client";
import { FormGroup, Input, MarkerProps, showSnackbar } from "@cofano/cck";
import { Box } from "@mui/material";

import DatePicker from "components/cck/DatePicker";
import { ExceptionErrorContent } from "components/utils/Request2";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import NumberInput from "components/cck/NumberInput";

import ShipSelect from "view/ships/components/selects/ShipSelect";

import { Language } from "utils";

import { mapSimulatorSchema, MapSimulatorSchemaInput, MapSimulatorSchemaOutput } from "../../utils/map.schema";
import SimulatorMarker from "../markers/SimulatorMarker";

const MapSimulatorTab = ({ position, onChange }: {
	position?: { latitude: number; longitude: number };
	onChange: (markers: MarkerProps[]) => void;
}) => {

	const { instance } = useCoastguard();

	const [ markers, setMarkers ] = useState<MarkerProps[]>([]);

	const { control, watch, handleSubmit } = useForm<MapSimulatorSchemaInput, unknown, MapSimulatorSchemaOutput>({
		resolver: zodResolver(mapSimulatorSchema),
		defaultValues: {
			ship: null,
			course: null,
			speed: null,
			timestamp: null,
			token: "",
		},
	});

	const values = watch();

	const save = (data: MapSimulatorSchemaOutput, currentPosition: { latitude: number; longitude: number }) => {

		const newMarkers = [ ...markers, {
			lat: currentPosition.latitude,
			lng: currentPosition.longitude,
			component: () => <SimulatorMarker number={markers.length + 1} />,
		} ];

		setMarkers(newMarkers);
		onChange(newMarkers);

		instance.fetch("/qewidata", {
			method: "POST",
			body: JSON.stringify({
				speed: data.speed,
				course: data.course,
				source: "simulator",
				position: {
					latitude: currentPosition.latitude,
					longitude: currentPosition.longitude,
				},
				object: {
					trackerId: data.ship.mmsi,
				},
				timestamp: data.timestamp ?? moment().unix() * 1000,
			}),
			mode: "cors",
			headers: {
				"Content-Type": "application/json",
				"Cache-Control": "no-cache",
				"Pragma": "no-cache",
				"Authorization": `Bearer ${values.token}`,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "OK" }), { variant: "success" });
		}).catch(error => {
			showSnackbar(<ExceptionErrorContent response={{ error: { code: error.status, message: error.message } }} />, { variant: "error" });
		});
	};

	useEffect(() => {
		if (position) {
			handleSubmit((formData) => {
				save(formData, position);
			})();
		}
	}, [ position ]);

	return (
		<Box p={2}>
			<FormGroup
				label={Language("SHIPS_SINGLE")}
				labelPlacement="left-center"
				overflow="hidden"
				required>
				<Controller
					name="ship"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<ShipSelect {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup
				label={Language("MAPS_COURSE")}
				labelPlacement="left-center"
				overflow="hidden"
				required>
				<Controller
					name="course"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<NumberInput {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup
				label={Language("MAPS_SPEED")}
				labelPlacement="left-center"
				overflow="hidden"
				required>
				<Controller
					name="speed"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<NumberInput {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup
				label={Language("TIMESTAMP")}
				labelPlacement="left-center"
				overflow="hidden">
				<Controller
					name="timestamp"
					control={control}
					render={({ field }) => <DatePicker {...field} showTimeInput />} />
			</FormGroup>

			<FormGroup
				label={Language("TOKEN")}
				labelPlacement="left-center"
				overflow="hidden"
				required>
				<Controller
					name="token"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>
		</Box>
	);
};

export default MapSimulatorTab;
